/* eslint-disable react/no-array-index-key */
import { useMemo, useState, useEffect } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';
import { makeStyles } from '@material-ui/core/styles';

import Properties, { ROW_TYPES, renderPageLink } from '../../properties/Properties';
import { useInterface, timeRangeFilterId, interfaceFilterId } from '../../filters';
import { bitRateFormatter, logFormatter, DEFAULT_INVALID_VALUE } from '../../../utilities/FormattingUtilities';
import { useCurrentFilterStateById } from '../../../stores/FilterStateStore';
import { useDataService } from '../../../services/ApiDataService';
import PropertiesSiteList from '../../properties/PropertiesSiteList';
import { stringProperty, linkProperty, listLinkProperty } from '../../properties/funcs';
import { panelPropDefaults, panelPropTypes } from '../../page-framework/panel/PanelPropTypes';
import useStyles from '../../page-framework/panel/PanelStyles';
import Panel from '../../page-framework/panel/PanelDecorator';

const useCbQosStyles = makeStyles({
    cbqos: {
        paddingLeft: '13px',
    },
});

const cbQosTitleProperties = [
    {
        title: '',
        titleOnly: true,
        type: ROW_TYPES.TEXT,
    },
    {
        title: t`CBQoS Discards`,
        titleOnly: true,
        type: ROW_TYPES.TEXT,
    },
];

/**
 * CBQoS classes are typically only assigned to policies in one direction, but can be in both
 * directions. In that case, append ' (In)' or ' (Out)' to the label to differentiate them.
 * @param {Object[]} qosClasses CBQoS classes array
 * @param {Object} qosClass CBQoS class object
 * @returns {*}
 */
function buildCbQosClassName(qosClasses, qosClass) {
    const count = qosClasses.reduce((sum, record) => (record.name === qosClass.name ? sum + 1 : sum), 0);
    const direction = qosClass?.direction === 1 ? 'In' : 'Out';
    return count < 2 ? qosClass.name : `${qosClass.name} (${direction})`;
}

/**
 * Build the C* metric name from the metric base name stored in the class
 * @param qosClass
 * @returns {string}
 */
function buildCbQosMetricName(qosClass) {
    const direction = qosClass?.direction === 1 ? 'In' : 'Out';
    return `cbQosDiscardCount${direction}_${qosClass.metric}`;
}

/**
 * Glue the various sets of data together (cbQosClasses, interface, sites for device IP, and metrics)
 * @param cbQosData
 * @param interfaceData
 * @param sitesData
 * @param metricsData
 * @returns {*}
 */
function buildPropertiesData(cbQosData, interfaceData, sitesData, metricsData) {
    interfaceData.cbQosDefs = cbQosData;
    interfaceData.metrics = metricsData;
    interfaceData.sites = sitesData;

    // Create a "displaySpeed" property which contains either the configuredSpeed (if > 0),
    // or else the inSpeed (if configuredSpeed has not been set). Use a negative value as a
    // flag that it was configured.
    interfaceData.displaySpeed = interfaceData?.configuredSpeed
        ? -interfaceData.configuredSpeed
        : interfaceData?.inSpeed;

    return interfaceData;
}

/**
 * Build a set of CBQoS properties definitions for the enabled CBQoS classes on this interface,
 * sorted by the title.
 * @param data
 * @param className
 * @returns {[Object]}
 */
function getCbQosProperties(data, className) {
    const { cbQosDefs } = data;
    if (cbQosDefs && data?.cbQosClasses?.length) {
        const properties = data.cbQosClasses
            .filter((qosClass) => isEnabledClass(qosClass, cbQosDefs))
            .map((qosClass) => {
                const metric = buildCbQosMetricName(qosClass);
                return {
                    title: buildCbQosClassName(data.cbQosClasses, qosClass),
                    type: ROW_TYPES.FUNCTION,
                    mapper: (d) => {
                        const value = d?.metrics?.[metric];
                        return _.isNumber(value) ? logFormatter(value, { maxDecimals: 0 }) : DEFAULT_INVALID_VALUE;
                    },
                    className,
                };
            });
        return _.concat(cbQosTitleProperties, _.orderBy(properties, [(prop) => prop.title.toLowerCase()], ['asc']));
    }
    return [];
}

/**
 * Build the set of parameters for a /query/table request on the count metrics for this
 * panel, with both the standard set of metrics and any CBQoS metrics this interface may have.
 * @param uuid
 * @param start
 * @param end
 * @param data
 * @param cbQosClasses
 * @returns {Object}
 */
function getTableRequestParams(uuid, start, end, data, cbQosClasses) {
    let cbQosMetrics = [];
    if (data?.cbQosClasses?.length) {
        cbQosMetrics = data.cbQosClasses
            .filter((qosClass) => isEnabledClass(qosClass, cbQosClasses))
            .map((qosClass) => {
                return buildCbQosMetricName(qosClass);
            });
    }
    return {
        type: 'interface',
        interface: uuid,
        renderAsEntity: true,
        start,
        end,
        metrics: [
            'packetsInCount',
            'packetsOutCount',
            'errorsInCount',
            'errorsOutCount',
            'discardsInCount',
            'discardsOutCount',
            ...cbQosMetrics,
        ],
    };
}

/**
 * Check to see if the specified class name/direction has monitoring enabled.
 * @param qosClass
 * @param cbQosClasses
 * @returns {boolean}
 */
function isEnabledClass(qosClass, cbQosClasses) {
    return (
        _.findIndex(cbQosClasses, { metric: qosClass.metric, direction: qosClass.direction, enableMonitoring: true }) >=
        0
    );
}

function renderBitRateFormatter(data) {
    const formattedData = bitRateFormatter(Math.abs(data));
    if (data < 0) {
        return <strong>{formattedData}</strong>;
    }
    return formattedData;
}

function renderLogFormatter(data) {
    return logFormatter(data, { maxDecimals: 0 });
}

function renderSiteList(value) {
    return <PropertiesSiteList value={value} excludedFilters={[interfaceFilterId]} />;
}

/**
 * Build the properties column definition, including a link to the parent Device and CBQoS metrics (if any)
 * @param data
 * @param deviceId
 * @returns {[Object]}
 */
function usePropertiesColumns(data, deviceId) {
    const classes = useCbQosStyles();
    return useMemo(() => {
        return [
            {
                properties: [
                    stringProperty(t`Name`, 'name'),
                    linkProperty(
                        t`Device Name`,
                        'deviceName',
                        '/device-detail',
                        [{ name: 'Device', uuid: deviceId }],
                        [interfaceFilterId],
                        renderPageLink
                    ),
                    stringProperty(t`Description`, 'description'),
                    stringProperty(t`Type`, 'ifType'),
                    stringProperty(t`Speed`, 'displaySpeed', renderBitRateFormatter),
                    stringProperty(t`Duplex`, 'duplex'),
                    stringProperty(t`Device IP`, 'deviceIpAddress'),
                    listLinkProperty(t`Sites`, 'sites', [], [interfaceFilterId], renderSiteList),
                ],
            },
            {
                properties: [
                    stringProperty(t`Packets In`, 'metrics.packetsInCount', renderLogFormatter),
                    stringProperty(t`Packets Out`, 'metrics.packetsOutCount', renderLogFormatter),
                    stringProperty(t`Errors In`, 'metrics.errorsInCount', renderLogFormatter),
                    stringProperty(t`Errors Out`, 'metrics.errorsOutCount', renderLogFormatter),
                    stringProperty(t`Discards In`, 'metrics.discardsInCount', renderLogFormatter),
                    stringProperty(t`Discards Out`, 'metrics.discardsOutCount', renderLogFormatter),
                    ...getCbQosProperties(data, classes.cbqos),
                ],
            },
        ];
    }, [deviceId, data, classes.cbqos]);
}

/**
 * Combine data from multiple queries, including a table query built from the CBQos class
 * names for this interface (if any).
 * @returns {Object}
 */
function usePropertiesData() {
    const intf = useInterface();
    const { uuid, deviceIpAddress, hasCbQosClasses } = intf || {};
    const dataService = useDataService();
    const [props, setProps] = useState();
    const [timeRangeFilterState] = useCurrentFilterStateById(timeRangeFilterId);
    const { start, end } = timeRangeFilterState || {};
    const startTime = start ? start.getTime() : null;
    const endTime = end ? end.getTime() : null;

    useEffect(() => {
        if (!uuid || !startTime) {
            return;
        }

        const cbQosRequest = hasCbQosClasses
            ? dataService.get('/ngp/v2/cbqos-classes').then((result) => result?.data)
            : Promise.resolve(null);
        const interfaceRequest = dataService.get(`/ipm/v1/open/interfaces/${uuid}`);
        const sitesRequest = dataService.get('/query/admin/sites/whichIp', { ip: deviceIpAddress });

        Promise.all([cbQosRequest, interfaceRequest, sitesRequest]).then(([cbQosData, interfaceData, sitesData]) => {
            const metricsRequest = dataService.get(
                '/query/table',
                getTableRequestParams(uuid, startTime, endTime, interfaceData, cbQosData)
            );
            metricsRequest
                .then((result) => result?.data?.[0])
                .then((metricsData) => {
                    setProps(buildPropertiesData(cbQosData, interfaceData, sitesData, metricsData));
                });
        });
    }, [dataService, uuid, deviceIpAddress, startTime, endTime, hasCbQosClasses]);

    return props;
}

export function InterfaceProperties({ data }) {
    const classes = useStyles();
    const intf = useInterface();
    const columns = usePropertiesColumns(data, intf?.device?.uuid);
    return (
        <div className={classes.propertiesPanel}>
            {columns.map((def, index) => (
                <Properties propertiesDef={def} data={data} key={`column-${index}`} />
            ))}
        </div>
    );
}

export function InterfacePropertiesPanel() {
    const data = usePropertiesData();
    if (!data) return null;
    return <InterfaceProperties data={data} />;
}

InterfacePropertiesPanel.propTypes = panelPropTypes;

InterfacePropertiesPanel.defaultProps = {
    ...panelPropDefaults,
    title: t`Interface Properties`,
    autoHeight: true,
    hideActions: true,
    minContentHeight: 200,
};

export default Panel({
    typeAlias: 'InterfaceAttributes',
    filters: {
        required: [timeRangeFilterId, interfaceFilterId],
        optional: [],
    },
})(InterfacePropertiesPanel);
